import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { useState } from 'react';
import { Button, IconButton, TextField } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import './TelephoneAgentSetting.css';
import ErrorHelper from '@/component/common/ErrorHelper';
import { validateHoldWarningTime, validateToolsCallTimeout } from '../handler/ProjectProfileManagementHandler';
import HoldAudioFileUpload from './HoldAudioFileUpload';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

function TelephoneAgentSetting({
    projectInuseDetail,
    setIsEdited,
    projectType,
    isUsingTelephoneAgent,
    setIsUsingTelephoneAgent,
    phoneNumbers,
    setPhoneNumbers,
    holdMusicFileS3Key,
    holdMusicTmpS3Key,
    setHoldMusicTmpS3Key,
    holdMusicRemoved,
    setHoldMusicRemoved,
    holdWarningTimeMinutes,
    setHoldWarningTimeMinutes,
    toolsCallTimeoutSeconds,
    setToolsCallTimeoutSeconds,
    error,
    setError
}) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const DEFAULT_COUNTRY = {country: 'jp', dialCode: '81'};

    const [isIncomingCallRestriction, setIsIncomingCallRestriction] = useState(false);
    const [blockedPhoneNumbers, setBlockedPhoneNumbers] = useState('');

    const hasValidPhoneNumber = () => {
        if (Utils.isEmptyArray(phoneNumbers)) return false;
        return phoneNumbers.some(phone => phone.tel && phone.tel !== phone.dialCode);
    };

    const handleToggleUsingTelephone = () => {
        setIsEdited(true);
        const isUsing = !isUsingTelephoneAgent;
        setIsUsingTelephoneAgent(isUsing);

        // Toggle OFF → clear all err of telephone
        if (!isUsing) {
            setPhoneNumbers(prev => prev.map(phone => ({ ...phone, error: '' })));
        }
    }

    const addPhoneNumber = () => {
        if (isUsingTelephoneAgent) {
            setIsEdited(false);
        };
        setPhoneNumbers(prev => [...prev, { id: uuidv4(), tel: '', dialCode: DEFAULT_COUNTRY.dialCode }]);
    };

    const deletePhoneNumber = (id) => {
        setIsEdited(true);
        setPhoneNumbers(prev => prev.filter((phone) => phone.id !== id));
    };

    const handlePhoneNumberChange = (id, value, data) => {
        setIsEdited(true);

        const dialCode = data?.dialCode?.toString() ?? "";

        setPhoneNumbers(prev =>
            prev.map(phone =>
                phone.id === id
                    ? {
                        ...phone,
                        tel: value,
                        dialCode,
                        error: ''
                    }
                    : phone
            )
        );
    };

    const handlePhoneNumberBlur = (e, phoneData) => {
        // skip validate when toggle OFF
        if (!isUsingTelephoneAgent) return;

        const blurValue = e.target.value;

        setPhoneNumbers(prev => {
            let hasError = false;
          
            const next = prev.map(phone => {
              if (phone.id !== phoneData.id) return phone;
          
              if (!blurValue || blurValue === `+${phoneData.dialCode}`) {
                return {...phone, error: ''};
              }
          
              const duplicated = prev.some(
                p => p.id !== phoneData.id && `+${p.tel}` === blurValue
              );
          
              if (duplicated) {
                hasError = true;
                return {...phone, error: trI18n('MSG_TELEPHONE_TICKET_DUPLICATED_PROJECT_TEL_NUMBER')};
              }
          
              return {...phone, error: ''};
            });
          
            setIsEdited(!hasError);
            return next;
          });
    };

    // ===== Hold Warning Time Handlers =====
    const handleHoldWarningTimeChange = (e) => {
        // Only accept half-width digits. Strips full-width alphanumeric, hiragana,
        // full-width katakana, etc. entered via IME so they never reach the state.
        const value = e.target.value.replace(/[^0-9]/g, '');
        setHoldWarningTimeMinutes(value);
        setIsEdited(true);
    };

    const handleHoldWarningTimeBlur = (e) => {
        const value = e.target.value;
        validateHoldWarningTime(value, error, setError);
    };

    const handleToolsCallTimeoutChange = (e) => {
        // Half-width digits only, same as the hold warning time input.
        const value = e.target.value.replace(/[^0-9]/g, '');
        setToolsCallTimeoutSeconds(value);
        setIsEdited(true);
    };

    const handleToolsCallTimeoutBlur = (e) => {
        validateToolsCallTimeout(e.target.value, error, setError);
    };


    return (
        <div className="groupSetting category-active">
            <div className="advanced-setting-title">
                <h3>{trI18n('TELEPHONE_AGENT_FUNCTION')}</h3>
            </div>
            {projectType === Constant.PROJECT_SERVICE_TYPE.DESK_ON_PUBLIC_ON && (
                <>
                    <div className="project-email-setting-group telephone-setting-function">
                        <div className="title-name">{trI18n('EXTERNAL_PHONE_CALL')}</div>
                        <div className="switch-check">
                            <input
                                name="isTelephoneSettingFunction"
                                id="is-telephone-setting-function-switch-id"
                                type="checkbox"
                                checked={isUsingTelephoneAgent}
                                onChange={handleToggleUsingTelephone}
                            />
                            <label htmlFor="is-telephone-setting-function-switch-id"></label>
                        </div>
                    </div>
                    <div className="project-email-setting-group email-setting-function">
                        <div className="title-name">
                            {trI18n('TELEPHONE_NUMBER')}
                        </div>
                    </div>
                    {isUsingTelephoneAgent && !hasValidPhoneNumber() && (
                        <div className="project-email-setting-group email-setting-function">
                            <div className="telephone-warning-message">
                                {trI18n('TELEPHONE_NUMBER_REQUIRED_FOR_EXTERNAL_CALL')}
                            </div>
                        </div>
                    )}
                    <div className="project-email-setting-group email-setting-function">
                        <div className="telephone-setting-content">
                            <Button
                                className="btn-add-phone"
                                startIcon={<Add />}
                                onClick={addPhoneNumber}
                                disabled={!isUsingTelephoneAgent}
                            >
                                {trI18n('TELEPHONE_NUMBER_ADD')}
                            </Button>
                            <div className="phone-number-list">
                                {!Utils.isEmptyArray(phoneNumbers) && phoneNumbers.map((phone) => (
                                    <div className="phone-input-group" key={phone.id}>
                                        <div className="phone-number-item">
                                            <PhoneInput
                                                country={DEFAULT_COUNTRY.country}
                                                value={phone.tel}
                                                disabled={!isUsingTelephoneAgent}
                                                onChange={(value, data) => handlePhoneNumberChange(phone.id, value, data)}
                                                onBlur={e => handlePhoneNumberBlur(e, phone)}
                                                autoFormat={false}
                                                countryCodeEditable={false}
                                            />
                                            <IconButton
                                                className="delete-button"
                                                onClick={() => deletePhoneNumber(phone.id)}
                                                disabled={!isUsingTelephoneAgent}
                                            >
                                                <img src="/assets/image/delete-icon.svg" alt="" />
                                            </IconButton>
                                        </div>
                                        <ErrorHelper
                                            isShowError={phone.error}
                                            textError={phone.error}
                                            isShowNoticeIcon={false}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Hold Audio File */}
            <HoldAudioFileUpload
                projectId={projectInuseDetail.id}
                holdMusicFileS3Key={holdMusicFileS3Key}
                holdMusicTmpS3Key={holdMusicTmpS3Key}
                setHoldMusicTmpS3Key={setHoldMusicTmpS3Key}
                holdMusicRemoved={holdMusicRemoved}
                setHoldMusicRemoved={setHoldMusicRemoved}
                setIsEdited={setIsEdited}
                error={error}
                setError={setError}
            />

            {/* Hold Warning Time */}
            <div className="project-email-setting-group email-setting-function">
                <div className="title-name">{trI18n('HOLD_WARNING_TIME')}</div>
            </div>
            <div className="project-email-setting-group email-setting-function">
                <div className="hold-warning-time-content">
                    <TextField
                        fullWidth
                        variant="outlined"
                        className="form-control-wrapInput form-control-hg40"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }}
                        value={holdWarningTimeMinutes}
                        onKeyPress={Utils.onKeyPressNumber}
                        onPaste={Utils.handleOnPasteNumber}
                        onChange={handleHoldWarningTimeChange}
                        onBlur={handleHoldWarningTimeBlur}
                        error={!!error?.holdWarningTimeMinutes}
                        helperText={error?.holdWarningTimeMinutes}
                    />
                </div>
            </div>

            {/* Tools call timeout (AI tự tra cứu trong cuộc gọi) */}
            <div className="project-email-setting-group telephone-setting-function">
                <div className="title-name">{trI18n('TOOLS_CALL_TIMEOUT')}</div>
            </div>
            <div className="project-email-setting-group email-setting-function">
                <TextField
                    fullWidth
                    variant="outlined"
                    className="form-control-wrapInput form-control-hg40"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 3 }}
                    value={toolsCallTimeoutSeconds}
                    onKeyPress={Utils.onKeyPressNumber}
                    onPaste={Utils.handleOnPasteNumber}
                    onChange={handleToolsCallTimeoutChange}
                    onBlur={handleToolsCallTimeoutBlur}
                    error={!!error?.toolsCallTimeoutSeconds}
                    helperText={error?.toolsCallTimeoutSeconds}
                />
            </div>

            {/* out of scope */}
            {false && (
                <>
                    <div className="separator-line"></div>
                    <div className="project-email-setting-group telephone-setting-function">
                        <div className="title-name">{trI18n('TELEPHONE_NUMBER_RESTRICT')}</div>
                        <div className="switch-check">
                            <input
                                name="isIncomingCallRestriction"
                                id="is-incoming-call-restriction-switch-id"
                                type="checkbox"
                                checked={isIncomingCallRestriction}
                                onChange={e => {
                                    setIsIncomingCallRestriction(!isIncomingCallRestriction);
                                }}
                            />
                            <label htmlFor="is-incoming-call-restriction-switch-id"></label>
                        </div>
                    </div>
                    {isIncomingCallRestriction && (
                        <div className="project-email-setting-group email-setting-function">
                            <div className="telephone-setting-content">
                                <div className="description-text">
                                    {trI18n('TELEPHONE_NUMBER_RESTRICT_DESCRIPTION')}
                                    <br />
                                    {trI18n('TELEPHONE_NUMBER_RESTRICT_MAX')}
                                </div>
                                <TextField
                                    className="blocked-phone-numbers-input"
                                    multiline
                                    rows={6}
                                    variant="outlined"
                                    value={blockedPhoneNumbers}
                                    onChange={e => setBlockedPhoneNumbers(e.target.value)}
                                    fullWidth
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TelephoneAgentSetting;

TelephoneAgentSetting.propTypes = {
    setIsEdited: PropTypes.func.isRequired,
    projectType: PropTypes.string.isRequired,
    isUsingTelephoneAgent: PropTypes.bool.isRequired,
    setIsUsingTelephoneAgent: PropTypes.func.isRequired,
    phoneNumbers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            tel: PropTypes.string.isRequired,
            dialCode: PropTypes.string,
            error: PropTypes.string,
        })
    ).isRequired,
    setPhoneNumbers: PropTypes.func.isRequired,
    holdMusicFileS3Key: PropTypes.string,
    holdMusicTmpS3Key: PropTypes.string,
    setHoldMusicTmpS3Key: PropTypes.func.isRequired,
    holdMusicRemoved: PropTypes.bool,
    setHoldMusicRemoved: PropTypes.func.isRequired,
    holdWarningTimeMinutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setHoldWarningTimeMinutes: PropTypes.func.isRequired,
    toolsCallTimeoutSeconds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setToolsCallTimeoutSeconds: PropTypes.func.isRequired,
    error: PropTypes.object,
    setError: PropTypes.func.isRequired,
};
